import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Download, RotateCcw, X, ZoomIn, ZoomOut } from 'lucide-react';
import './ImageLightbox.css';

const MIN_SCALE = 1;
const MAX_SCALE = 5;
const ZOOM_STEP = 0.35;

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const normalizeScale = (value) => clamp(Number(value) || MIN_SCALE, MIN_SCALE, MAX_SCALE);

const getPointerDistance = (first, second) => {
  const dx = first.clientX - second.clientX;
  const dy = first.clientY - second.clientY;
  return Math.hypot(dx, dy);
};

function ImageLightbox({
  src,
  alt = 'Imagem',
  title,
  downloadName,
  onClose,
  initialScale = MIN_SCALE,
  showDownload = true,
}) {
  const [scale, setScale] = useState(() => normalizeScale(initialScale));
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const viewportRef = useRef(null);
  const imageRef = useRef(null);
  const activePointersRef = useRef(new Map());
  const dragRef = useRef(null);
  const pinchRef = useRef(null);
  const didDragRef = useRef(false);

  const canPan = scale > MIN_SCALE;
  const caption = title || downloadName || '';
  const zoomPercent = `${Math.round(scale * 100)}%`;

  const resetView = useCallback(() => {
    activePointersRef.current.clear();
    dragRef.current = null;
    pinchRef.current = null;
    didDragRef.current = false;
    setIsDragging(false);
    setScale(normalizeScale(initialScale));
    setOffset({ x: 0, y: 0 });
  }, [initialScale]);

  const clampPanOffset = useCallback((nextOffset, nextScale = scale) => {
    const viewport = viewportRef.current;
    const image = imageRef.current;
    if (!viewport || !image || nextScale <= MIN_SCALE) {
      return nextScale <= MIN_SCALE ? { x: 0, y: 0 } : nextOffset;
    }

    const viewportRect = viewport.getBoundingClientRect();
    const scaledWidth = image.offsetWidth * nextScale;
    const scaledHeight = image.offsetHeight * nextScale;
    const maxX = Math.max(0, (scaledWidth - viewportRect.width) / 2);
    const maxY = Math.max(0, (scaledHeight - viewportRect.height) / 2);

    return {
      x: maxX > 0 ? clamp(nextOffset.x, -maxX, maxX) : 0,
      y: maxY > 0 ? clamp(nextOffset.y, -maxY, maxY) : 0,
    };
  }, [scale]);

  const setClampedScale = useCallback((resolveNextScale) => {
    setScale((current) => {
      const rawNextScale =
        typeof resolveNextScale === 'function' ? resolveNextScale(current) : resolveNextScale;
      const nextScale = normalizeScale(rawNextScale);
      setOffset((currentOffset) => clampPanOffset(currentOffset, nextScale));
      return nextScale;
    });
  }, [clampPanOffset]);

  useEffect(() => {
    resetView();
  }, [resetView, src]);

  useEffect(() => {
    if (!src || typeof document === 'undefined') return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose?.();
      }
      if ((event.key === '+' || event.key === '=') && !event.ctrlKey && !event.metaKey) {
        event.preventDefault();
        setClampedScale((current) => current + ZOOM_STEP);
      }
      if (event.key === '-' && !event.ctrlKey && !event.metaKey) {
        event.preventDefault();
        setClampedScale((current) => current - ZOOM_STEP);
      }
      if (event.key === '0') {
        event.preventDefault();
        resetView();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose, resetView, setClampedScale, src]);

  useEffect(() => {
    if (scale <= MIN_SCALE) {
      setOffset({ x: 0, y: 0 });
    }
  }, [scale]);

  useEffect(() => {
    if (!src || typeof window === 'undefined') return undefined;

    const handleResize = () => {
      setOffset((current) => clampPanOffset(current, scale));
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [clampPanOffset, scale, src]);

  const updateScaleAroundPoint = useCallback((nextScale, clientX, clientY) => {
    setScale((currentScale) => {
      const normalizedNext = normalizeScale(nextScale);
      if (normalizedNext === currentScale) return currentScale;

      setOffset((currentOffset) => {
        const viewport = viewportRef.current;
        if (!viewport) return currentOffset;
        const rect = viewport.getBoundingClientRect();
        const originX = clientX - rect.left - rect.width / 2;
        const originY = clientY - rect.top - rect.height / 2;
        const ratio = normalizedNext / currentScale;

        if (normalizedNext <= MIN_SCALE) {
          return { x: 0, y: 0 };
        }

        return clampPanOffset({
          x: originX - (originX - currentOffset.x) * ratio,
          y: originY - (originY - currentOffset.y) * ratio,
        }, normalizedNext);
      });

      return normalizedNext;
    });
  }, [clampPanOffset]);

  const handleZoomIn = useCallback(() => {
    setClampedScale((current) => current + ZOOM_STEP);
  }, [setClampedScale]);

  const handleZoomOut = useCallback(() => {
    setClampedScale((current) => current - ZOOM_STEP);
  }, [setClampedScale]);

  const handleWheel = useCallback((event) => {
    event.preventDefault();
    const direction = event.deltaY > 0 ? -1 : 1;
    updateScaleAroundPoint(scale + direction * ZOOM_STEP, event.clientX, event.clientY);
  }, [scale, updateScaleAroundPoint]);

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!src || !viewport) return undefined;

    viewport.addEventListener('wheel', handleWheel, { passive: false });
    return () => {
      viewport.removeEventListener('wheel', handleWheel);
    };
  }, [handleWheel, src]);

  const handlePointerDown = useCallback((event) => {
    if (event.button !== undefined && event.button !== 0) return;

    event.currentTarget.setPointerCapture?.(event.pointerId);
    activePointersRef.current.set(event.pointerId, {
      clientX: event.clientX,
      clientY: event.clientY,
    });
    didDragRef.current = false;

    if (activePointersRef.current.size === 2) {
      const pointers = Array.from(activePointersRef.current.values());
      pinchRef.current = {
        distance: getPointerDistance(pointers[0], pointers[1]),
        scale,
      };
      dragRef.current = null;
      setIsDragging(false);
      return;
    }

    if (!canPan) return;

    dragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      offsetX: offset.x,
      offsetY: offset.y,
    };
    setIsDragging(true);
  }, [canPan, offset.x, offset.y, scale]);

  const handlePointerMove = useCallback((event) => {
    if (!activePointersRef.current.has(event.pointerId)) return;

    activePointersRef.current.set(event.pointerId, {
      clientX: event.clientX,
      clientY: event.clientY,
    });

    if (activePointersRef.current.size >= 2 && pinchRef.current) {
      const pointers = Array.from(activePointersRef.current.values());
      const distance = getPointerDistance(pointers[0], pointers[1]);
      const nextScale = normalizeScale(
        pinchRef.current.scale * (distance / Math.max(pinchRef.current.distance, 1))
      );
      setScale(nextScale);
      setOffset((current) => clampPanOffset(current, nextScale));
      return;
    }

    const dragState = dragRef.current;
    if (!dragState || dragState.pointerId !== event.pointerId || !canPan) return;

    const dx = event.clientX - dragState.startX;
    const dy = event.clientY - dragState.startY;
    if (Math.abs(dx) > 2 || Math.abs(dy) > 2) {
      didDragRef.current = true;
    }
    setOffset(clampPanOffset({
      x: dragState.offsetX + dx,
      y: dragState.offsetY + dy,
    }, scale));
  }, [canPan, clampPanOffset, scale]);

  const releasePointer = useCallback((event) => {
    activePointersRef.current.delete(event.pointerId);
    event.currentTarget.releasePointerCapture?.(event.pointerId);

    if (activePointersRef.current.size < 2) {
      pinchRef.current = null;
    }

    if (dragRef.current?.pointerId === event.pointerId) {
      dragRef.current = null;
      setIsDragging(false);
    }
  }, []);

  const handleImageClick = useCallback((event) => {
    event.stopPropagation();
    if (didDragRef.current) {
      didDragRef.current = false;
      return;
    }
    updateScaleAroundPoint(scale >= MAX_SCALE ? MIN_SCALE : scale + ZOOM_STEP, event.clientX, event.clientY);
  }, [scale, updateScaleAroundPoint]);

  const imageStyle = useMemo(() => ({
    transform: `translate3d(${offset.x}px, ${offset.y}px, 0) scale(${scale})`,
  }), [offset.x, offset.y, scale]);

  if (!src || typeof document === 'undefined') return null;

  return createPortal(
    <div
      className="image-lightbox"
      role="dialog"
      aria-modal="true"
      aria-label={caption ? `Visualização de ${caption}` : 'Visualização de imagem'}
      onClick={(event) => {
        if (event.target === event.currentTarget) {
          onClose?.();
        }
      }}
    >
      <div className="image-lightbox__toolbar" onClick={(event) => event.stopPropagation()}>
        <button
          type="button"
          className="image-lightbox__button"
          onClick={handleZoomOut}
          disabled={scale <= MIN_SCALE}
          title="Diminuir zoom"
          aria-label="Diminuir zoom"
        >
          <ZoomOut size={18} />
        </button>
        <span className="image-lightbox__scale" aria-live="polite">{zoomPercent}</span>
        <button
          type="button"
          className="image-lightbox__button"
          onClick={handleZoomIn}
          disabled={scale >= MAX_SCALE}
          title="Aumentar zoom"
          aria-label="Aumentar zoom"
        >
          <ZoomIn size={18} />
        </button>
        <button
          type="button"
          className="image-lightbox__button"
          onClick={resetView}
          title="Redefinir zoom"
          aria-label="Redefinir zoom"
        >
          <RotateCcw size={18} />
        </button>
        {showDownload && (
          <a
            className="image-lightbox__button"
            href={src}
            download={downloadName || true}
            target="_blank"
            rel="noopener noreferrer"
            title="Baixar imagem"
            aria-label="Baixar imagem"
          >
            <Download size={18} />
          </a>
        )}
        <button
          type="button"
          className="image-lightbox__button"
          onClick={onClose}
          title="Fechar"
          aria-label="Fechar visualização"
        >
          <X size={18} />
        </button>
      </div>
      <div
        ref={viewportRef}
        className={`image-lightbox__viewport${canPan ? ' image-lightbox__viewport--can-pan' : ''}${isDragging ? ' is-dragging' : ''}`}
        onClick={(event) => {
          event.stopPropagation();
        }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={releasePointer}
        onPointerCancel={releasePointer}
      >
        <img
          ref={imageRef}
          src={src}
          alt={alt}
          className="image-lightbox__image"
          style={imageStyle}
          draggable={false}
          onClick={handleImageClick}
          onLoad={() => {
            setOffset((current) => clampPanOffset(current, scale));
          }}
        />
      </div>
      {caption && <div className="image-lightbox__caption">{caption}</div>}
    </div>,
    document.body
  );
}

export default ImageLightbox;
